import { Flex, Select, TextInput } from "@gravity-ui/uikit";
import { ChangeEvent, useMemo, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import InputMask from "react-input-mask";

import { CountryCode, countryPhoneData } from "./countryPhoneData";
import css from "./PhoneInput.module.scss";
import { createPatternFromMask, getCountryMask } from "./PhoneInput.utils";

interface PhoneInputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  errorMessage?: string;
}

export const PhoneInput = <T extends FieldValues>({
  control,
  name,
  required = false,
  errorMessage,
}: PhoneInputProps<T>) => {
  const [selectedCountry, setSelectedCountry] = useState(CountryCode.RU);

  const mask = useMemo(
    () => getCountryMask(selectedCountry),
    [selectedCountry],
  );
  const pattern = useMemo(() => createPatternFromMask(mask), [mask]);

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required ? "Обязательное поле" : false,
        pattern: {
          value: pattern,
          message: errorMessage || "Неверный формат номера телефона",
        },
      }}
      render={({ field, fieldState }) => (
        <Flex direction="column">
          <Flex className={css.phoneInputContainer}>
            <div className={css.countrySelect}>
              <Select
                value={[selectedCountry]}
                onUpdate={(value) => {
                  if (value.length > 0) {
                    const country = value[0] as CountryCode;
                    setSelectedCountry(country);
                  }
                }}
                size="xl"
                placeholder="Страна"
                popupClassName={css.countrySelectPopup}
                controlClassName={css.countrySelectControl}
              >
                {countryPhoneData.map((country) => (
                  <Select.Option key={country.code} value={country.code}>
                    <Flex
                      alignItems="center"
                      gap={2}
                      className={css.countryOption}
                    >
                      <img
                        src={`https://flagicons.lipis.dev/flags/4x3/${country.code.toLowerCase()}.svg`}
                        width={20}
                        height={20}
                      />
                      <span>{country.name}</span>
                      <span className={css.countryCode}>
                        {country.dialCode}
                      </span>
                    </Flex>
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className={css.phoneInput}>
              <InputMask
                mask={mask}
                maskChar="_"
                alwaysShowMask
                value={field.value || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e);
                }}
              >
                {(inputProps) => (
                  <TextInput
                    {...inputProps}
                    size="xl"
                    type="tel"
                    validationState={fieldState.invalid ? "invalid" : undefined}
                    errorMessage={fieldState.error?.message}
                    errorPlacement="outside"
                  />
                )}
              </InputMask>
            </div>
          </Flex>
        </Flex>
      )}
    />
  );
};
